/**
 * Work Orders Page
 * Vista para gestionar órdenes de trabajo de tratamientos de control de plagas
 */

import { useState, useEffect } from "react";
import { Icon, ConfirmModal } from "@/components/ui";
import styles from "./WorkOrdersPage.module.css";

interface Client {
  id: string;
  name: string;
  address: string;
  phone: string;
}

interface Technician {
  id: string;
  name: string;
  initials: string;
}

interface Pest {
  id: string;
  name: string;
  icon?: string;
}

interface Material {
  id: string;
  name: string;
  quantity: number;
  used?: number;
}

interface ActionStep {
  id: string;
  order: number;
  description: string;
  completed: boolean;
  notes?: string;
}

interface WorkOrder {
  id: string;
  orderNumber: string;
  client: Client;
  technician: Technician | null;
  pest: Pest;
  status: "pending" | "assigned" | "inProgress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  scheduledDate: string;
  completedDate?: string;
  startTime?: string;
  endTime?: string;
  totalMinutes?: number;
  actionPlan: ActionStep[];
  materials: Material[];
  observations?: string;
  clientSignature?: string;
  technicianSignature?: string;
  photos?: string[];
  createdAt: string;
}

// Mock data
const mockTechnicians: Technician[] = [
  { id: "1", name: "Juan Pérez", initials: "JP" },
  { id: "2", name: "María García", initials: "MG" },
  { id: "3", name: "Carlos Rodríguez", initials: "CR" },
];

const mockWorkOrders: WorkOrder[] = [
  {
    id: "1",
    orderNumber: "OT-2026-001",
    client: {
      id: "1",
      name: "Restaurante El Buen Sabor",
      address: "Calle Mayor 45, Madrid",
      phone: "911234567",
    },
    technician: mockTechnicians[0],
    pest: { id: "1", name: "Cucarachas", icon: "🪳" },
    status: "assigned",
    priority: "high",
    scheduledDate: "2026-01-22",
    actionPlan: [
      {
        id: "1",
        order: 1,
        description: "Inspección completa del perímetro",
        completed: false,
      },
      {
        id: "2",
        order: 2,
        description: "Aplicación de gel insecticida en puntos estratégicos",
        completed: false,
      },
    ],
    materials: [
      { id: "1", name: "Gel insecticida Maxforce", quantity: 2 },
      { id: "2", name: "Trampas adhesivas", quantity: 6 },
    ],
    createdAt: "2026-01-21T10:30:00",
  },
  {
    id: "2",
    orderNumber: "OT-2026-002",
    client: {
      id: "2",
      name: "Hotel Plaza Central",
      address: "Avenida Principal 123, Barcelona",
      phone: "934567890",
    },
    technician: mockTechnicians[1],
    pest: { id: "2", name: "Ratas", icon: "🐀" },
    status: "inProgress",
    priority: "urgent",
    scheduledDate: "2026-01-21",
    startTime: "2026-01-21T09:30:00",
    totalMinutes: 85,
    actionPlan: [
      {
        id: "1",
        order: 1,
        description: "Localizar madrigueras y puntos de acceso",
        completed: true,
        notes: "Se encontraron 3 puntos de entrada en sótano",
      },
      {
        id: "2",
        order: 2,
        description: "Colocar estaciones porta-cebos",
        completed: false,
      },
    ],
    materials: [
      { id: "1", name: "Rodenticida anticoagulante", quantity: 2, used: 1 },
      { id: "2", name: "Estaciones porta-cebos", quantity: 8, used: 5 },
    ],
    createdAt: "2026-01-20T14:15:00",
  },
  {
    id: "3",
    orderNumber: "OT-2026-003",
    client: {
      id: "3",
      name: "Oficinas TechCorp",
      address: "Polígono Industrial Norte, Nave 5",
      phone: "956789012",
    },
    technician: null,
    pest: { id: "1", name: "Cucarachas", icon: "🪳" },
    status: "pending",
    priority: "medium",
    scheduledDate: "2026-01-23",
    actionPlan: [
      {
        id: "1",
        order: 1,
        description: "Inspección de oficinas y zonas comunes",
        completed: false,
      },
    ],
    materials: [{ id: "1", name: "Insecticida residual", quantity: 500 }],
    createdAt: "2026-01-21T09:00:00",
  },
];

export function WorkOrdersPage() {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>(mockWorkOrders);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Actualizar tiempo cada minuto para órdenes en progreso
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(timer);
  }, []);

  const getStatusLabel = (status: WorkOrder["status"]): string => {
    const labels = {
      pending: "Pendiente",
      assigned: "Asignada",
      inProgress: "En Proceso",
      completed: "Completada",
      cancelled: "Cancelada",
    };
    return labels[status];
  };

  const getStatusColor = (status: WorkOrder["status"]): string => {
    const colors = {
      pending: styles.statusPending,
      assigned: styles.statusAssigned,
      inProgress: styles.statusInProgress,
      completed: styles.statusCompleted,
      cancelled: styles.statusCancelled,
    };
    return colors[status];
  };

  const getPriorityLabel = (priority: WorkOrder["priority"]): string => {
    const labels = {
      low: "Baja",
      medium: "Media",
      high: "Alta",
      urgent: "Urgente",
    };
    return labels[priority];
  };

  const getPriorityColor = (priority: WorkOrder["priority"]): string => {
    const colors = {
      low: styles.priorityLow,
      medium: styles.priorityMedium,
      high: styles.priorityHigh,
      urgent: styles.priorityUrgent,
    };
    return colors[priority];
  };

  const getFilteredOrders = () => {
    return workOrders.filter((order) => {
      const matchesStatus =
        filterStatus === "all" || order.status === filterStatus;
      const matchesPriority =
        filterPriority === "all" || order.priority === filterPriority;
      const matchesSearch =
        searchTerm === "" ||
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.pest.name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesStatus && matchesPriority && matchesSearch;
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const calculateElapsedTime = (
    order: WorkOrder,
  ): { hours: number; minutes: number } | null => {
    if (!order.startTime) return null;

    const startTime = new Date(order.startTime).getTime();
    const endTime = order.endTime
      ? new Date(order.endTime).getTime()
      : currentTime.getTime();
    const elapsedMinutes = Math.floor((endTime - startTime) / 60000);

    return {
      hours: Math.floor(elapsedMinutes / 60),
      minutes: elapsedMinutes % 60,
    };
  };

  const handleCancelOrder = (order: WorkOrder) => {
    setConfirmModal({
      isOpen: true,
      title: "Cancelar Orden de Trabajo",
      message: `¿Estás seguro de que deseas cancelar la orden ${order.orderNumber}? Esta acción no se puede deshacer.`,
      onConfirm: () => {
        // TODO: Implementar cancelación
        console.log("Orden cancelada:", order.id);
        setConfirmModal({ ...confirmModal, isOpen: false });
      },
    });
  };

  const handleToggleStep = (orderId: string, stepId: string) => {
    setWorkOrders((orders) =>
      orders.map((order) => {
        if (order.id === orderId) {
          const updatedActionPlan = order.actionPlan.map((step) =>
            step.id === stepId ? { ...step, completed: !step.completed } : step,
          );
          const updatedOrder = { ...order, actionPlan: updatedActionPlan };

          // Actualizar también selectedOrder si es la orden actual
          if (selectedOrder?.id === orderId) {
            setSelectedOrder(updatedOrder);
          }

          return updatedOrder;
        }
        return order;
      }),
    );
  };

  const filteredOrders = getFilteredOrders();

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <Icon name="clipboard" size={32} />
            <div>
              <h1>Órdenes de Trabajo</h1>
              <p>Gestión y seguimiento de tratamientos de control de plagas</p>
            </div>
          </div>
          <button className={styles.newButton}>
            <Icon name="plus" size={18} />
            Nueva Orden
          </button>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <Icon name="search" size={18} />
            <input
              type="text"
              placeholder="Buscar por número, cliente o plaga..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="assigned">Asignadas</option>
            <option value="inProgress">En Proceso</option>
            <option value="completed">Completadas</option>
            <option value="cancelled">Canceladas</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">Todas las prioridades</option>
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Orders List */}
        <div className={styles.ordersList}>
          {filteredOrders.length === 0 ? (
            <div className={styles.emptyState}>
              <Icon name="clipboard" size={64} />
              <p>No se encontraron órdenes de trabajo</p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order.id}
                className={`${styles.orderCard} ${
                  selectedOrder?.id === order.id ? styles.selected : ""
                }`}
                onClick={() => setSelectedOrder(order)}
              >
                <div className={styles.orderCardHeader}>
                  <div className={styles.orderNumber}>
                    <span className={styles.pestIcon}>{order.pest.icon}</span>
                    <div>
                      <strong>{order.orderNumber}</strong>
                      <span className={styles.pestName}>{order.pest.name}</span>
                    </div>
                  </div>
                  <div className={styles.badges}>
                    <span
                      className={`${styles.statusBadge} ${getStatusColor(order.status)}`}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                    <span
                      className={`${styles.priorityBadge} ${getPriorityColor(order.priority)}`}
                    >
                      {getPriorityLabel(order.priority)}
                    </span>
                  </div>
                </div>

                <div className={styles.orderCardBody}>
                  <div className={styles.orderInfo}>
                    <Icon name="user" size={16} />
                    <span>{order.client.name}</span>
                  </div>
                  <div className={styles.orderInfo}>
                    <Icon name="mapPin" size={16} />
                    <span>{order.client.address}</span>
                  </div>
                  <div className={styles.orderInfo}>
                    <Icon name="calendar" size={16} />
                    <span>Programada: {formatDate(order.scheduledDate)}</span>
                  </div>
                  {order.technician && (
                    <div className={styles.orderInfo}>
                      <Icon name="users" size={16} />
                      <span>Técnico: {order.technician.name}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Order Details */}
        {selectedOrder && (
          <div className={styles.orderDetails}>
            <div className={styles.detailsHeader}>
              <div className={styles.detailsHeaderRow}>
                <div className={styles.detailsTitle}>
                  <span className={styles.detailsPestIcon}>
                    {selectedOrder.pest.icon}
                  </span>
                  <div>
                    <h2>{selectedOrder.orderNumber}</h2>
                    <p>{selectedOrder.pest.name}</p>
                  </div>
                </div>
                <div className={styles.detailsActions}>
                  <button className={styles.editButton} title="Editar">
                    <Icon name="edit" size={18} />
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleCancelOrder(selectedOrder)}
                    title="Cancelar"
                  >
                    <Icon name="xCircle" size={18} />
                  </button>
                </div>
              </div>
              <div className={styles.detailsBadges}>
                <span
                  className={`${styles.statusBadge} ${getStatusColor(selectedOrder.status)}`}
                >
                  {getStatusLabel(selectedOrder.status)}
                </span>
                <span
                  className={`${styles.priorityBadge} ${getPriorityColor(selectedOrder.priority)}`}
                >
                  {getPriorityLabel(selectedOrder.priority)}
                </span>
              </div>
            </div>

            <div className={styles.detailsContent}>
              {/* Client Info */}
              <section className={styles.section}>
                <h3>
                  <Icon name="user" size={20} />
                  Información del Cliente
                </h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <label>Cliente</label>
                    <span>{selectedOrder.client.name}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Dirección</label>
                    <span>{selectedOrder.client.address}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Teléfono</label>
                    <span>{selectedOrder.client.phone}</span>
                  </div>
                </div>
              </section>

              {/* Schedule Info */}
              <section className={styles.section}>
                <h3>
                  <Icon name="calendar" size={20} />
                  Programación y Tiempo
                </h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <label>Fecha Programada</label>
                    <span>{formatDate(selectedOrder.scheduledDate)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Técnico Asignado</label>
                    <span>
                      {selectedOrder.technician?.name || "Sin asignar"}
                    </span>
                  </div>
                  {selectedOrder.completedDate && (
                    <div className={styles.infoItem}>
                      <label>Fecha Completada</label>
                      <span>{formatDate(selectedOrder.completedDate)}</span>
                    </div>
                  )}
                  {selectedOrder.startTime && (
                    <div className={styles.infoItem}>
                      <label>Inicio del Trabajo</label>
                      <span>
                        {new Date(selectedOrder.startTime).toLocaleTimeString(
                          "es-ES",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  )}
                  {selectedOrder.endTime && (
                    <div className={styles.infoItem}>
                      <label>Fin del Trabajo</label>
                      <span>
                        {new Date(selectedOrder.endTime).toLocaleTimeString(
                          "es-ES",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const elapsed = calculateElapsedTime(selectedOrder);
                    if (!elapsed) return null;

                    return (
                      <div className={styles.infoItem}>
                        <label>
                          {selectedOrder.endTime
                            ? "Tiempo Total"
                            : "Tiempo Transcurrido"}
                        </label>
                        <span
                          className={
                            selectedOrder.endTime
                              ? styles.timeWorked
                              : styles.timeRunning
                          }
                        >
                          {String(elapsed.hours).padStart(2, "0")}:
                          {String(elapsed.minutes).padStart(2, "0")}
                          {!selectedOrder.endTime && (
                            <span className={styles.liveDot}>●</span>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                  {selectedOrder.status === "inProgress" &&
                    !selectedOrder.endTime &&
                    selectedOrder.startTime && (
                      <div className={styles.infoItem}>
                        <label>Estado Actual</label>
                        <span className={styles.workingNow}>
                          <Icon name="clock" size={16} />
                          Trabajando ahora
                        </span>
                      </div>
                    )}
                </div>
              </section>

              {/* Action Plan */}
              <section className={styles.section}>
                <h3>
                  <Icon name="list" size={20} />
                  Plan de Actuación
                </h3>
                <div className={styles.actionPlan}>
                  {selectedOrder.actionPlan.map((step) => (
                    <div
                      key={step.id}
                      className={`${styles.actionStep} ${
                        step.completed ? styles.completed : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={step.completed}
                        disabled
                        className={styles.stepCheckbox}
                      />
                      <div className={styles.stepNumber}>{step.order}</div>
                      <div className={styles.stepContent}>
                        <p>{step.description}</p>
                        {step.notes && (
                          <span className={styles.stepNotes}>
                            <Icon name="info" size={14} />
                            {step.notes}
                          </span>
                        )}
                      </div>
                      {step.completed && <Icon name="check" size={20} />}
                    </div>
                  ))}
                </div>
              </section>

              {/* Materials */}
              <section className={styles.section}>
                <h3>
                  <Icon name="package" size={20} />
                  Materiales
                </h3>
                <div className={styles.materialsList}>
                  {selectedOrder.materials.map((material) => (
                    <div key={material.id} className={styles.materialItem}>
                      <span className={styles.materialName}>
                        {material.name}
                      </span>
                      <span className={styles.materialQuantity}>
                        {material.used !== undefined
                          ? `${material.used} / ${material.quantity}`
                          : material.quantity}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
}
